import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  browserSessionPersistence,
  setPersistence,
} from "firebase/auth";
import { getFirebaseAuth } from "@/lib/firebase";

/**
 * Initialize auth persistence to session-only (cleared on browser close)
 */
export async function initializeAuthPersistence(): Promise<void> {
  const auth = getFirebaseAuth();
  await setPersistence(auth, browserSessionPersistence);
}

/**
 * Sign in with email and password
 */
export async function signInWithEmail(
  email: string,
  password: string
): Promise<void> {
  const auth = getFirebaseAuth();
  await signInWithEmailAndPassword(auth, email, password);
}

/**
 * Create a new account with email and password
 */
export async function createAccountWithEmail(
  email: string,
  password: string
): Promise<void> {
  const auth = getFirebaseAuth();
  await createUserWithEmailAndPassword(auth, email, password);
}

/**
 * Sign out the current user
 */
export async function signOutUser(): Promise<void> {
  const auth = getFirebaseAuth();
  await firebaseSignOut(auth);
}

/**
 * Send password reset email
 */
export async function sendPasswordReset(email: string): Promise<void> {
  const auth = getFirebaseAuth();
  await sendPasswordResetEmail(auth, email);
}

/**
 * Map Firebase auth error codes to user-friendly messages
 */
export function getAuthErrorMessage(errorCode: string): string {
  const errorMessages: Record<string, string> = {
    "auth/invalid-email": "auth.errors.invalidEmail",
    "auth/user-disabled": "auth.errors.userDisabled",
    "auth/user-not-found": "auth.errors.userNotFound",
    "auth/wrong-password": "auth.errors.wrongPassword",
    "auth/invalid-credential": "auth.errors.invalidCredential",
    "auth/email-already-in-use": "auth.errors.emailInUse",
    "auth/weak-password": "auth.errors.weakPassword",
    "auth/operation-not-allowed": "auth.errors.operationNotAllowed",
    "auth/too-many-requests": "auth.errors.tooManyRequests",
    "auth/network-request-failed": "auth.errors.networkError",
  };

  return errorMessages[errorCode] || "auth.errors.generic";
}
